/**
 * #2096 — the pure half of the chat data layer: shapes, limits and errors.
 *
 * Split out of `chatService.js` so it can be exercised directly. The service
 * imports axios, `./api` (which pulls in Vue, Vuex and the store) and the
 * Firebase SDK through dynamic imports — none of which load under plain node.
 * Everything in THIS file is a function of its arguments, so the translation
 * between what cs3-chat sends and what the #2091 panel was written against is
 * actually tested rather than merely asserted about.
 *
 * Nothing here performs I/O. If a function in this file needs a network call
 * or a global, it belongs in `chatService.js` instead.
 */

/** cs3-chat refuses any single query over 120 (spec decision #14). */
export const MAX_SCROLLBACK = 120;

/** What a channel opens with. */
export const PAGE_SIZE = 50;

/** The chat service's per-message ceiling (spec §5). */
export const MAX_MESSAGE_CHARS = 4000;

/**
 * Clamp a requested scrollback to what Firestore will actually serve.
 *
 * ⚠ The Firestore rules REFUSE a larger query, and a refused listener is a
 * silent empty panel rather than an error — so the cap is applied in code
 * where it can be read, not left to the rules to enforce invisibly.
 */
export function clampScrollback(limit) {
    return Math.max(1, Math.min(MAX_SCROLLBACK, Number(limit) || PAGE_SIZE));
}

// ─────────────────────────────────────────────────────────────────────────────
// Shapes. The panel was written against #2091's invented objects, so the live
// data is translated here rather than in the component — one place to look when
// the API changes, and the panel stays a panel.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * One channel, as `GET /api/clients/{id}/channels` returns it.
 *
 * ⚠ THESE ARE THE API'S FIELD NAMES, WHICH ARE *NOT* THE FIRESTORE DOCUMENT'S.
 *
 * This is an easy and silent mistake, so it is worth stating: the channel
 * exists in two places with two different shapes, and the panel needs both.
 *
 *   the API (`ChannelAdmin::present`)   snake_case roster — id, kind, scope,
 *                                       client_id, key, name, archived,
 *                                       own_option_on, starts_at, ends_at
 *   Firestore `channels/{id}`           camelCase LIVE state — lastMessageId,
 *                                       lastSender, lastSnippet, lastAt
 *
 * So the roster comes from here and the "last message" preview that drives the
 * channel list and the toasts comes from `applyChannelDoc` below — spec §7:
 * "Each panel listens to its channels' docs to drive toasts". Reading
 * `lastSnippet` off the API response yields an empty preview and an unread
 * count stuck at zero, with nothing in any log to say why.
 */
export function normaliseChannel(raw) {
    const r = raw || {};

    const channel = {
        id: r.id,
        name: r.name || r.id,
        label: r.name || r.id,
        // 'cs' | 'client' | 'global', matching #2091's grouping.
        grp: r.scope === 'cs' ? 'cs' : (r.scope === 'global' ? 'global' : 'client'),
        kind: r.kind || null,
        /*
         * #2098 — the channel's key, which the service has always sent and the
         * panel had no use for until direct messages.
         *
         * ⚠ FOR A DIRECT CHANNEL IT IS THE PAIR OF UIDS (`uidA.uidB`, sorted —
         * cs3-chat `Channel::pairKey`), and it is the only thing on a roster row
         * that says WHO the conversation is with: the stored name is symmetric
         * and the row carries no member list. Without it the panel cannot label
         * a conversation after a reload, and cannot watch the right person's
         * presence. Null for the kinds that have no key.
         */
        key: r.key === undefined || r.key === null || r.key === '' ? null : String(r.key),
        archived: !!r.archived,
        // Live state, filled in by applyChannelDoc when Firestore sends it.
        unread: 0,
        last: '',
        at: '—',
    };

    /*
     * The job window, which the service DOES send and which #2091 r3 already
     * knows how to render ("Chat for this job opens Tue 08:00"). `starts_at` /
     * `ends_at` are the job's own times and `own_option_on` is the job's Chat
     * switch — the second half of the client master switch (spec decision #27).
     *
     * Shaped as `job: {chatOn, start, end}` because that is what
     * `jobChatWindow()` takes; the 24-hours-either-side arithmetic stays in one
     * place rather than being redone here.
     */
    if (r.kind === 'job' && (r.starts_at || r.ends_at)) {
        channel.job = {
            chatOn: r.own_option_on !== false,
            start: r.starts_at || null,
            end: r.ends_at || null,
        };
    }

    // The employer's own Chat switch, the same two-switch rule for employers.
    if (r.kind === 'employer' && r.own_option_on !== undefined) {
        channel.employer = { chatOn: r.own_option_on !== false };
    }

    return channel;
}

/**
 * Merge a Firestore `channels/{id}` document into a channel from the roster.
 *
 * Returns true when the last message CHANGED, which is the panel's signal to
 * bump an unread count and raise a toast. `lastMessageId` rather than the
 * snippet: an identical message posted twice is two events, and a snapshot
 * that fires without the message changing (a membership edit, a reconnect)
 * is not one.
 *
 * Mutates in place so Vue's existing reactive channel object keeps its
 * identity — replacing it would drop the row's component state.
 */
export function applyChannelDoc(channel, doc) {
    const d = doc || {};
    const changed = !!d.lastMessageId && d.lastMessageId !== channel.lastMessageId;

    channel.lastMessageId = d.lastMessageId || channel.lastMessageId || null;
    /*
     * #2110 — the sender's uid is kept for the toast's avatar colour (the same
     * key the message bubbles use) and NOTHING else. It used to decide "is this
     * mine" for the unread count; the server's count leaves out a person's own
     * messages, so that comparison is gone.
     */
    channel.lastSenderUid = d.lastSenderUid || null;

    if (d.lastSnippet) {
        channel.last = (d.lastSender ? d.lastSender + ': ' : '') + d.lastSnippet;
        /*
         * #2110 — and the two halves separately, for the toast. The toast used
         * to be built from the Firestore document at the moment it arrived; it
         * is now raised when the SERVER's count for the channel rises, which is
         * a moment later and has no document in hand.
         */
        channel.lastSender = d.lastSender || '';
        channel.lastSnippet = d.lastSnippet;
    }
    if (d.lastAt) {
        channel.at = shortStamp(d.lastAt);
    }
    if (d.archived !== undefined) {
        // The server can archive a channel while someone is looking at it, and
        // an archived channel is read-only for everyone (spec §3 rule 5).
        channel.archived = !!d.archived;
    }

    return changed;
}

/**
 * Attachment metadata from a message document, normalised.
 *
 * cs3-chat publishes `{id, name, type, size}` and NOTHING else — no URL and no
 * object key (#25 item 7), because a message document is readable by every
 * member for ninety days and a pre-signed URL is a bearer token nobody can
 * revoke. So the panel fetches a link per view, from the endpoint that re-asks
 * membership.
 *
 * @return {Array<{id: string, name: string, type: string, size: number, thumbnail: boolean}>}
 */
export function normaliseAttachments(raw) {
    if (!Array.isArray(raw)) return [];

    return raw
        .map((a) => {
            const one = a || {};
            const type = String(one.type || '');

            return {
                id: String(one.id || ''),
                name: String(one.name || '').trim() || 'attachment',
                type,
                size: Number(one.size) || 0,
                /*
                 * ⚠ HEIC IS NOT A THUMBNAIL, and this is the one piece of
                 * browser reality in this file. Safari renders HEIC; Chrome and
                 * Firefox do not, so an `<img>` would be a broken-image icon in
                 * most of the fleet. A file chip is honest everywhere, and the
                 * file still downloads and opens in the OS viewer.
                 */
                thumbnail: type === 'image/jpeg' || type === 'image/png',
            };
        })
        .filter((a) => a.id !== '');
}

export function normaliseMessage(id, raw) {
    const r = raw || {};
    const at = toDate(r.createdAt);

    /*
     * Resolve the name ONCE and derive the avatar from the resolved value. A
     * fallback applied to the label but not to the initials gives a message
     * headed "CallSteward user" with a "?" avatar beside it — two different
     * answers to the same question, on the same line.
     */
    const name = String(r.name == null ? '' : r.name).trim() || 'CallSteward user';

    return {
        id,
        uid: r.uid || null,
        who: {
            n: name,
            i: initialsFor(name),
            r: r.role || 'member',
            c: colourFor(r.uid || name),
        },
        staff: r.role === 'cs_admin' || r.role === 'cs_support',
        day: at ? dayLabel(at) : '',
        at: at ? stamp(at) : '',
        body: String(r.body == null ? '' : r.body),
        links: Array.isArray(r.links) ? r.links : [],
        /*
         * #2102 — the attachment METADATA cs3-chat publishes: id, name, type,
         * size, and nothing else (#25 item 7). There is no URL in a message
         * document and there must never be one: the document is readable by
         * every member for ninety days, and a pre-signed URL is a bearer token
         * nobody can revoke. The panel exchanges the id for a short-lived link
         * at the moment the file is viewed, through the endpoint that re-asks
         * membership.
         *
         * Absent on every message posted before #25, which is why this reads as
         * an empty array rather than undefined.
         */
        attachments: normaliseAttachments(r.attachments),
    };
}

/**
 * Firestore timestamps arrive as a Timestamp object over the SDK and as
 * `{_seconds}` or an ISO string over other paths. All three happen, and the one
 * that is missed shows as "Invalid Date" in the panel rather than failing.
 */
function toDate(v) {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    if (typeof v === 'number') return new Date(v);
    if (typeof v === 'string') {
        const d = new Date(v);

        return isNaN(d.getTime()) ? null : d;
    }
    if (typeof v._seconds === 'number') return new Date(v._seconds * 1000);
    if (typeof v.seconds === 'number') return new Date(v.seconds * 1000);

    return null;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

/** "Monday 13 October" — the #2091 day-divider format. */
export function dayLabel(date) {
    return DAY_NAMES[date.getDay()] + ' ' + date.getDate() + ' ' + MONTHS[date.getMonth()];
}

function stamp(date) {
    return String(date.getHours()).padStart(2, '0') + ':' + String(date.getMinutes()).padStart(2, '0');
}

/** Today -> "09:12"; this week -> "Fri"; older -> "11 Oct". */
function shortStamp(v) {
    const d = toDate(v);
    if (!d) return '—';

    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return stamp(d);

    const ageDays = (now - d) / 86400000;
    if (ageDays < 7) return DAY_NAMES[d.getDay()].slice(0, 3);

    return d.getDate() + ' ' + MONTHS[d.getMonth()].slice(0, 3);
}

/**
 * "Dana Whitfield" -> "DW". Exported because the toast needs an avatar for a
 * sender it only knows by the name on a channel document.
 */
export function initialsFor(name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';

    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

/*
 * A stable avatar colour per person. Deterministic from the uid so the same
 * person is the same colour in every channel and across reloads — a palette
 * handed out in arrival order would reshuffle on every page load.
 */
const COLOURS = ['#7a5bb5', '#b5553f', '#2f7d64', '#3f6bbf', '#9a5b8c',
    '#5b6b8c', '#7a6b2f', '#3f8c8c', '#8c5b3f', '#5b8c3f'];

export function colourFor(key) {
    let h = 0;
    const s = String(key);
    for (let i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) & 0x7fffffff;
    }

    return COLOURS[h % COLOURS.length];
}

/**
 * The roster endpoint — the channels THIS user may read.
 *
 * ⚠ NOT `/api/clients/{cid}/channels`, which is the Configuration → Chat ADMIN
 * listing and is gated on cs3-chat's `ChatPolicy::canManageClient`. The panel
 * called that one for every user, so a Client Admin got their channels and
 * every other role got 403 and no chat (measured on stage 2026-10-19).
 * cs3-chat #24 added this one for the question the panel actually asks.
 *
 * A constant so the path is pinned by a test: it is a contract with another
 * repo, and pointing it back at the admin listing is the regression to catch.
 */
export const ROSTER_PATH = '/api/me/channels';

/** The admin channel listing. Here only so a test can assert the roster is NOT it. */
export const ADMIN_CHANNELS_PATH = '/api/clients/';

/**
 * The roster's HTTP status → the code the panel explains.
 *
 * Pure and here, rather than inline in `chatService.js`, for the same reason
 * `settingsFailureCode` is in `chatSettings.js`: the service module pulls in
 * axios and `./api` (Vue, Vuex, the store) and does not load under node, so an
 * inline mapping is the one piece that cannot be tested — and this particular
 * mapping is the one that showed a permission refusal as "Chat is unreachable".
 */
export function rosterFailureCode(status) {
    return {
        401: 'chat_session_expired',
        // A permission answer, not a network one. Before #2096's review round
        // this fell through to the catch-all, which is exactly what Ed saw.
        403: 'chat_not_permitted',
        // The chat box predates cs3-chat #24, so the roster endpoint is not
        // there. A deploy, not a network fault.
        404: 'chat_roster_unavailable',
    }[status] || 'chat_unreachable';
}

/** Every failure the panel can be asked to explain, with a code it can switch on. */
export class ChatUnavailable extends Error {
    constructor(code, cause) {
        super(code);
        this.name = 'ChatUnavailable';
        this.code = code;
        this.cause = cause || null;
    }
}
